import { useState, useCallback, useEffect, useRef } from 'react';
import api from '../../../../../../services/api';
import { newResId, blankResource, blankBOQItem, CURRENCIES, calcSummary } from './commercialsUtils';

const useCommercialsEditor = ({ rfpId, commercialsData, onSaveSuccess }) => {
    const initState = useCallback(() => {
        const raw = commercialsData || {};
        const d = raw.commercials || raw;
        return {
            pricingModel: d.pricing_model || 'Fixed Price',
            currencyCode: d.currency || 'AED',
            currencySymbol: d.currency_symbol || 'AED',
            resources: (d.resources || []).map((r) => ({ ...r, _id: newResId() })),
            additionalCosts: (d.additional_costs || []).map((c) => ({ ...c, _id: newResId() })),
            boq: (d.boq || []).map((b) => ({
                ...b,
                _id: newResId(),
                item_name: b.item_name || '',
                description: b.description || '',
                model: b.model || b.model_specs || '',
                model_specs: b.model || b.model_specs || '',
                quantity: String(b.quantity || ''),
                unit: b.unit || '',
                amount: b.amount || b.estimated_amount || '',
                estimated_amount: b.amount || b.estimated_amount || '',
            })),
            params: {
                overhead_pct: d.params?.overhead_pct ?? 12,
                buffer_pct: d.params?.buffer_pct ?? 10,
                profit_margin_pct: d.params?.profit_margin_pct ?? 20,
                tax_pct: (d.params?.tax_pct === undefined || d.params?.tax_pct === null || d.params?.tax_pct === 18) ? 5 : d.params.tax_pct,
                discount_pct: d.params?.discount_pct ?? 0,
            },
        };
    }, [commercialsData]);

    const [state, setState] = useState(initState);
    const [isSaving, setIsSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState(null);
    const [saveError, setSaveError] = useState(null);

    const prevDataRef = useRef(commercialsData);
    useEffect(() => {
        if (commercialsData && commercialsData !== prevDataRef.current) {
            prevDataRef.current = commercialsData;
            setState(initState());
        }
    }, [commercialsData, initState]);

    const updateResource = (id, field, val) => {
        setState((prev) => ({
            ...prev,
            resources: prev.resources.map((r) => (r._id === id ? { ...r, [field]: val } : r)),
        }));
    };

    const addResource = () => {
        setState((prev) => ({ ...prev, resources: [...prev.resources, blankResource()] }));
    };

    const deleteResource = (id) => {
        setState((prev) => ({ ...prev, resources: prev.resources.filter((r) => r._id !== id) }));
    };

    const updateAddlCost = (id, field, val) => {
        setState((prev) => ({
            ...prev,
            additionalCosts: prev.additionalCosts.map((c) => (c._id === id ? { ...c, [field]: val } : c)),
        }));
    };

    const addAddlCost = () => {
        setState((prev) => ({
            ...prev,
            additionalCosts: [...prev.additionalCosts, { _id: newResId(), label: '', count: 1, cost_per_item: 0, amount: 0, total_cost: 0 }],
        }));
    };

    const deleteAddlCost = (id) => {
        setState((prev) => ({
            ...prev,
            additionalCosts: prev.additionalCosts.filter((c) => c._id !== id),
        }));
    };

    const updateBOQItem = (id, field, val) => {
        setState((prev) => ({
            ...prev,
            boq: prev.boq.map((b) => (b._id === id ? { ...b, [field]: val } : b)),
        }));
    };

    const addBOQItem = () => {
        setState((prev) => {
            const nextSl = prev.boq.length + 1;
            return {
                ...prev,
                boq: [...prev.boq, { ...blankBOQItem(), sl_no: String(nextSl) }]
            };
        });
    };

    const deleteBOQItem = (id) => {
        setState((prev) => {
            const filtered = prev.boq.filter((b) => b._id !== id);
            const resequenced = filtered.map((b, idx) => ({ ...b, sl_no: String(idx + 1) }));
            return {
                ...prev,
                boq: resequenced
            };
        });
    };

    const updateParam = (key, val) => {
        setState((prev) => ({
            ...prev,
            params: { ...prev.params, [key]: parseFloat(val) || 0 },
        }));
    };

    const buildPayload = () => {
        const selectedCurrency = CURRENCIES.find((c) => c.code === state.currencyCode) || CURRENCIES[0];
        
        const mappedResources = state.resources.map(({ _id, ...rest }) => {
            const count = parseInt(rest.count, 10) || 1;
            const months = parseFloat(rest.months) || 0;
            const cost_per_month = parseFloat(rest.cost_per_month) || 0;
            return {
                title: rest.title || '',
                level: rest.level || 'Mid',
                count,
                months,
                cost_per_month,
                total_cost: count * months * cost_per_month,
            };
        });

        const mappedAddlCosts = state.additionalCosts.map(({ _id, ...rest }) => {
            const count = parseInt(rest.count ?? 1, 10) || 1;
            const cost_per_item = parseFloat(rest.cost_per_item ?? rest.amount ?? 0) || 0;
            return {
                label: rest.label || '',
                count,
                cost_per_item,
                total_cost: count * cost_per_item,
            };
        });

        const mappedBOQ = (state.boq || []).map(({ _id, ...rest }) => ({
            sl_no: String(rest.sl_no || ''),
            item_name: rest.item_name || '',
            description: rest.description || '',
            model: rest.model || '',
            model_specs: rest.model || '',
            quantity: String(rest.quantity || ''),
            unit: rest.unit || '',
            amount: String(rest.amount || ''),
            estimated_amount: String(rest.amount || ''),
        }));

        const computedSummary = calcSummary(mappedResources, mappedAddlCosts, state.params, mappedBOQ);

        return {
            pricing_model: state.pricingModel,
            currency: selectedCurrency.code,
            currency_symbol: selectedCurrency.symbol,
            resources: mappedResources,
            additional_costs: mappedAddlCosts,
            boq: mappedBOQ,
            params: {
                overhead_pct: state.params.overhead_pct,
                buffer_pct: state.params.buffer_pct,
                profit_margin_pct: state.params.profit_margin_pct,
                tax_pct: state.params.tax_pct,
                discount_pct: state.params.discount_pct,
            },
            summary: {
                base_resource_cost: computedSummary.base_resource_cost,
                total_additional_cost: computedSummary.total_additional_cost,
                total_boq_cost: computedSummary.total_boq_cost,
                total_overhead: computedSummary.total_overhead,
                total_buffer: computedSummary.total_buffer,
                total_cost: computedSummary.total_cost,
                profit_margin_amt: computedSummary.profit_margin_amt,
                selling_price_pre_tax: computedSummary.selling_price_pre_tax,
                discount_amount: computedSummary.discount_amount,
                tax_amount: computedSummary.tax_amount,
                grand_total: computedSummary.grand_total,
                total_headcount: computedSummary.total_headcount,
            }
        };
    };

    const handleSave = async () => {
        if (!rfpId || isSaving) return;
        setIsSaving(true);
        setSaveError(null);
        try {
            const payload = buildPayload();
            const res = await api.put(`/extraction/v1/${rfpId}/commercials/cost-estimation`, payload);
            const returned = res.data?.commercials || res.data;
            if (returned) {
                setState((prev) => ({
                    ...prev,
                    pricingModel: returned.pricing_model || prev.pricingModel,
                    currencyCode: returned.currency || prev.currencyCode,
                    currencySymbol: returned.currency_symbol || prev.currencySymbol,
                    resources: (returned.resources || []).map((r) => ({ ...r, _id: newResId() })),
                    additionalCosts: (returned.additional_costs || []).map((c) => ({ ...c, _id: newResId() })),
                    boq: (returned.boq || []).map((b) => ({
                        ...b,
                        _id: newResId(),
                        item_name: b.item_name || '',
                        description: b.description || '',
                        model: b.model || b.model_specs || '',
                        model_specs: b.model || b.model_specs || '',
                        quantity: String(b.quantity || ''),
                        unit: b.unit || '',
                        amount: b.amount || b.estimated_amount || '',
                        estimated_amount: b.amount || b.estimated_amount || '',
                    })),
                    params: returned.params ? {
                        ...returned.params,
                        tax_pct: (returned.params.tax_pct === undefined || returned.params.tax_pct === null || returned.params.tax_pct === 18) ? 5 : returned.params.tax_pct
                    } : prev.params,
                }));
            }
            setLastSaved(new Date());
            if (typeof onSaveSuccess === 'function') {
                onSaveSuccess();
            }
        } catch (err) {
            console.error('[CommercialsEditor] Save failed:', err);
            setSaveError(err?.response?.data?.detail || 'Failed to save. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    return {
        state, setState,
        isSaving, lastSaved, saveError,
        updateResource, addResource, deleteResource,
        updateAddlCost, addAddlCost, deleteAddlCost,
        updateBOQItem, addBOQItem, deleteBOQItem,
        updateParam, handleSave
    };
};

export default useCommercialsEditor;
